import { Box, Button, Card, CardContent, Grid, Stack } from '@mui/material'
import Checkbox from 'components/forms/Checkbox'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import Select from 'components/forms/Select'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import StepperDialog from 'components/modal/StepperDialog'
import TooltipMUI from 'components/styled/TooltipMUI'
import { compact, isNil, isUndefined, orderBy, partition } from 'lodash'
import { REDUCER_DANGERS } from 'pgsse/constant/PgsseConstants'
import DtoDangerTable from 'pgsse/dto/DtoDangerTable'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import { LARGE_RADIUS } from 'utils/constants/Theme'
import useSandreList from 'utils/customHook/useSandreList'
import HelpRoundedIcon from '@mui/icons-material/HelpRounded'
import frequency from 'assets/pictures/pgsse/frequences.png'
import gravities from 'assets/pictures/pgsse/indices_gravite.png'
import detectability from 'assets/pictures/pgsse/detectabilite.png'
import { usedUrl } from 'conf/basepath'
import { useParams } from 'react-router'
import SimpleSelectionTable from 'components/datatable/SimpleSelectionTable'
import { useSelector } from 'react-redux'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import { INSTALLATION_TYPE } from 'installation/constants/InstallationConstants'

const STEP = {
    CHARACTERIZATION_STEP: 0,
    SELECT_STATIONS: 1,
    GROUP_STEP: 2,
    MASTERY_STEP: 3,
}

const RESOURCE = 1
const NETWORK = 5

const CharacterizationForm = ({
    danger = {},
    setDanger = () => {},
    readMode,
}) => {
    const structures = useSandreList(SANDRE.PGSSE_STRUCTURE)
    const families = useSandreList(`${SANDRE.PGSSE_FAMILLE}.${danger.idStructure}`)
    const steps = useSandreList(`${SANDRE.PGSSE_ETAPE}.${danger.idStructure}.${danger.familyType}`)
    const dangerTypes = useSandreList(SANDRE.PGSSE_TYPE_DANGER)
    const elements = useSandreList(SANDRE.PGSSE_CATEGORIE)
    const categories = danger.categories?.split(',').map(d => parseInt(d)) || []

    return (
        <Card>
            <CardContent>
                <Stack container spacing={1}>
                    <Select
                        label={i18n.structure}
                        value={danger.idStructure}
                        options={structures}
                        onChange={v => setDanger(prev => ({ ...prev, idStructure: v, familyType: undefined, step: undefined }))}
                        disabled={readMode}
                    />
                    <Select
                        label={i18n.familyDanger}
                        value={danger.familyType}
                        options={families}
                        onChange={v => setDanger(prev => ({ ...prev, familyType: v, step: undefined }))}
                        disabled={!danger.idStructure || readMode}
                    />
                    <Select
                        label={i18n.step}
                        value={danger.step}
                        options={steps}
                        onChange={v => setDanger(prev => ({ ...prev, step: v }))}
                        disabled={!danger.familyType || readMode}
                    />
                    <Select
                        label={i18n.dangerType}
                        value={danger.dangerType}
                        options={dangerTypes}
                        onChange={v => setDanger(prev => ({ ...prev, dangerType: v }))}
                        disabled={readMode}
                    />
                    <Grid container justifyContent={'space-between'}>
                        {
                            elements.map(e => (
                                <Checkbox
                                    key={e.name}
                                    label={e.name}
                                    checked={categories.includes(e.code)}
                                    onChange={v => setDanger(prev => ({
                                        ...prev,
                                        categories: v ? [...categories, e.code].join(',') : categories.filter(c => c !== e.code).join(','),
                                    }))}
                                    disabled={readMode}
                                />
                            ))
                        }
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    )
}

CharacterizationForm.propTypes = {
    danger: PropTypes.shape({}),
    setDanger: PropTypes.func,
    readMode: PropTypes.bool,
}

const SelectStationForm = ({
    danger = {},
    setDanger = () => {},
    isAllElements,
    setIsAllElements,
}) => {
    const {
        diagnosticElements,
        installations,
        contributors,
        cities,
    } = useSelector(store => ({
        diagnosticElements: store.PgsseReducer.diagnosticElements,
        installations: store.InstallationReducer.installationsIndex,
        contributors: store.ContributorReducer.contributorsIndex,
        cities: store.CityReducer.citiesIndex,
    }))

    const formattedData = orderBy((diagnosticElements.filter(de => {
        if (de.type !== STATION_TYPE_CONSTANT.INSTALLATION) {
            return false
        }
        const inst = installations[de.id] || {}
        if (danger.idStructure === RESOURCE) {
            return [INSTALLATION_TYPE.BOREHOLE, INSTALLATION_TYPE.CAPTURE].includes(inst.installationType)
        } else if (danger.idStructure === NETWORK) {
            return inst.installationType === INSTALLATION_TYPE.NETWORK
        }
        return ![INSTALLATION_TYPE.BOREHOLE, INSTALLATION_TYPE.CAPTURE, INSTALLATION_TYPE.NETWORK].includes(inst.installationType)
    }))
        .flatMap(d => {
            const inst = installations[d.id]
            return {
                ...inst,
                id: `${d.id}--${d.type}`,
                name: `${inst.name || ''}${inst.code && ` [${inst.code}]` || ''}`,
                city: cities[inst.townCode]?.name,
            }
        }), 'name')

    const formattedContributors = orderBy(diagnosticElements.filter(de => de.type !== STATION_TYPE_CONSTANT.INSTALLATION)
        .map(d => {
            const contributor = contributors[d.id] || {}
            return {
                ...contributor,
                id: `${d.id}--${d.type}`,
                name: `${contributor.name || ''}${contributor.code && ` [${contributor.code}]` || ''}`,
                city: cities[contributor.cityCode]?.name,
            }
        }), 'name')

    const [selectedStations, selectedContributors] = partition(danger.selectedStations, ss => parseInt(ss.split('--')?.[1]) === STATION_TYPE_CONSTANT.INSTALLATION)

    return (
        <Card>
            <CardContent>
                <Checkbox
                    label={i18n.applyOnAllElements}
                    checked={isAllElements}
                    onChange={setIsAllElements}
                />
                { !isAllElements && (
                    <>
                        <SimpleSelectionTable
                            onChange={v => setDanger({ ...danger, selectedStations: [...v, ...selectedContributors] })}

                            listData={formattedData}
                            selectedList={selectedStations}

                            listHeaders={['name', 'city']}
                            listTitle={i18n.nonSelectedInstallations}

                            selectedListTitle={i18n.selectedInstallations}
                        />
                        <SimpleSelectionTable
                            onChange={v => setDanger({ ...danger, selectedStations: [...v, ...selectedStations] })}

                            listData={formattedContributors}
                            selectedList={selectedContributors}

                            listHeaders={['name', 'city']}
                            listTitle={i18n.nonSelectedContributors}

                            selectedListTitle={i18n.selectedContributors}
                        />
                    </>
                )}
            </CardContent>
        </Card>
    )
}

SelectStationForm.propTypes = {
    danger: PropTypes.shape({}),
    setDanger: PropTypes.func,
    isAllElements: PropTypes.bool,
    setIsAllElements: PropTypes.func,
}

const GroupForm = ({
    danger = {},
    setDanger = () => {},
    readMode,
}) => {
    const groupDangers = useSandreList(SANDRE.PGSSE_GROUP_DANGER)
    const frequencies = useSandreList(SANDRE.PGSSE_FREQUENCE)
    const severities = useSandreList(SANDRE.PGSSE_GRAVITE)
    return (
        <Card>
            <CardContent>
                <Stack spacing={1}>
                    <Select
                        label={i18n.dangerGroup}
                        value={danger.dangerGroup}
                        options={groupDangers}
                        onChange={v => setDanger(prev => ({ ...prev, dangerGroup: v }))}
                        disabled={readMode}
                    />
                    <SimpleTextArea
                        title={i18n.precision}
                        value={danger.precision}
                        onChange={v => setDanger(prev => ({ ...prev, precision: v }))}
                        disabled={readMode}
                    />
                    <Grid container justifyContent='space-between' spacing={2} alignItems='center'>
                        <Grid size='auto'>
                            <TooltipMUI
                                title={(
                                    <Box sx={{ '& img': { width: 'auto', height: '20vh', borderRadius: LARGE_RADIUS } }}>
                                        <img src={frequency} alt='Image détaillant le paramètre' />
                                    </Box>
                                )}
                                placement='right'
                                sx={{ borderRadius: LARGE_RADIUS, padding: 0.5 }}
                            >
                                <HelpRoundedIcon />
                            </TooltipMUI>
                        </Grid>
                        <Grid size={3.6}>
                            <Select
                                label={i18n.frequency}
                                value={danger.frequency}
                                options={frequencies}
                                onChange={v => setDanger(prev => ({ ...prev, frequency: v }))}
                                disabled={readMode}
                            />
                        </Grid>
                        <Grid size='auto'>
                            <TooltipMUI
                                title={(
                                    <Box sx={{ '& img': { width: 'auto', height: '20vh', borderRadius: LARGE_RADIUS } }}>
                                        <img src={gravities} alt='Image détaillant le paramètre' />
                                    </Box>
                                )}
                                placement='right'
                                sx={{ borderRadius: LARGE_RADIUS, padding: 0.5 }}
                            >
                                <HelpRoundedIcon />
                            </TooltipMUI>
                        </Grid>
                        <Grid size={3.6}>
                            <Select
                                label={i18n.severity}
                                value={danger.severity}
                                options={severities}
                                onChange={v => setDanger(prev => ({ ...prev, severity: v }))}
                                disabled={readMode}
                            />
                        </Grid>
                        <Grid size={4}>
                            <NumberField
                                title={i18n.initialRisk}
                                value={danger.frequency * danger.severity}
                                disabled
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    )
}

GroupForm.propTypes = {
    danger: PropTypes.shape({}),
    setDanger: PropTypes.func,
    readMode: PropTypes.bool,
}

const MasteryForm = ({
    danger = {},
    setDanger = () => {},
    readMode,
}) => {
    const detectabilities = useSandreList(SANDRE.PGSSE_DETECTABILITE)
    return (
        <Card>
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={12}>
                        <Input
                            title={i18n.groupRisk}
                            value={danger.groupRisk}
                            onChange={v => setDanger(prev => ({ ...prev, groupRisk: v }))}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.implementation}
                            value={danger.implementation}
                            onChange={v => setDanger(prev => ({ ...prev, implementation: v }))}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Checkbox
                            label={i18n.measureTaken}
                            checked={danger.onSiteMeasure}
                            onChange={v => setDanger(prev => ({ ...prev, onSiteMeasure: v }))}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Input
                            title={i18n.measureFollowUp}
                            value={danger.followMeans}
                            onChange={v => setDanger(prev => ({ ...prev, followMeans: v }))}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.monitoringFrequency}
                            value={danger.monitoringFrequency}
                            onChange={v => setDanger(prev => ({ ...prev, monitoringFrequency: v }))}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.supervisor}
                            value={danger.monitoringContributor}
                            onChange={v => setDanger(prev => ({ ...prev, monitoringContributor: v }))}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={12}>
                        <SimpleTextArea
                            title={i18n.correctiveAction}
                            value={danger.correctionAction}
                            onChange={v => setDanger(prev => ({ ...prev, correctionAction: v }))}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid container size={6} alignItems='center'>
                        <Grid size={0.6}>
                            <TooltipMUI
                                title={(
                                    <Box sx={{ '& img': { width: 'auto', height: '20vh', borderRadius: LARGE_RADIUS } }}>
                                        <img src={detectability} alt='Image détaillant le paramètre' />
                                    </Box>
                                )}
                                placement='right'
                                sx={{ borderRadius: LARGE_RADIUS, padding: 0.5 }}
                            >
                                <HelpRoundedIcon />
                            </TooltipMUI>
                        </Grid>
                        <Grid size={11.4}>
                            <Select
                                label={i18n.detectability}
                                value={danger.detectability}
                                options={detectabilities}
                                onChange={v => setDanger(prev => ({ ...prev, detectability: v }))}
                                disabled={readMode}
                            />
                        </Grid>
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.residualRisk}
                            value={danger.detectability && ((danger.frequency * danger.severity) / danger.detectability)}
                            disabled
                        />
                    </Grid>
                    <Grid size={12}>
                        <SimpleTextArea
                            title={i18n.synthesis}
                            value={danger.synthesis}
                            onChange={v => setDanger(prev => ({ ...prev, synthesis: v }))}
                            rows={8}
                            disabled={readMode}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

MasteryForm.propTypes = {
    danger: PropTypes.shape({}),
    setDanger: PropTypes.func,
    readMode: PropTypes.bool,
}

const DangerDialog = ({
    danger: propsDanger = {},
    isOpen = false,
    setIsOpen = () => { },
    dispatchStateAUD,
    readMode,
}) => {
    const { idDiagnostic } = useParams()
    const [danger, setDanger] = useState(propsDanger)
    useEffect(() => setDanger(propsDanger), [propsDanger])

    const [isAllElements, setIsAllElements] = useState(true)

    const handleSave = () => {
        if (isNil(danger.index)) {
            if (!isAllElements) {
                dispatchStateAUD({
                    type: REDUCER_DANGERS.ADD,
                    payload: danger.selectedStations.map(d => {
                        const [siteCode, siteType] = d.split('--')
                        return {
                            ...danger,
                            siteCode: parseInt(siteCode),
                            siteType: parseInt(siteType),
                        }
                    }),
                })
            } else {
                dispatchStateAUD({ type: REDUCER_DANGERS.ADD, payload: [danger] })
            }
        } else {
            dispatchStateAUD({ type: REDUCER_DANGERS.UPDATE, payload: danger })
        }
        setIsOpen(false)
    }

    return (
        <StepperDialog
            steps={compact([
                {
                    label: i18n.characterization,
                    constant: STEP.CHARACTERIZATION_STEP,
                    nextAvailable: !isUndefined(danger.familyType),
                    nextStep: isNil(danger.index) ? STEP.SELECT_STATIONS : STEP.GROUP_STEP,
                },
                {
                    label: i18n.selectStations,
                    constant: STEP.SELECT_STATIONS,
                    hidden: !isNil(danger.index),
                },
                {
                    label: i18n.dangerGroup,
                    constant: STEP.GROUP_STEP,
                    previousStep: isNil(danger.index) ? STEP.SELECT_STATIONS : STEP.CHARACTERIZATION_STEP,
                },
                {
                    label: i18n.mastery,
                    constant: STEP.MASTERY_STEP,
                },
            ])}
            open={isOpen}
            title={isNil(danger.index) ? i18n.addDanger : i18n.editDanger }
            closeDialog={() => setIsOpen(false)}

            renderDeleteButton={() => (
                <Button
                    variant='outlined'
                    onClick={() => window.open(`${usedUrl}#/pgsse/diagnostic/${idDiagnostic}`, '_blank').focus()}
                >
                    {i18n.diagnostic}
                </Button>
            )}

            renderSaveButton={step => (step === STEP.MASTERY_STEP) && (
                <Button
                    variant='contained'
                    onClick={handleSave}
                >
                    {i18n.save}
                </Button>
            )}
        >
            {
                step => (
                    <>
                        {step === STEP.CHARACTERIZATION_STEP && (
                            <CharacterizationForm
                                danger={danger}
                                setDanger={setDanger}
                                readMode={readMode}
                            />
                        )}
                        {step === STEP.SELECT_STATIONS && (
                            <SelectStationForm
                                danger={danger}
                                setDanger={setDanger}
                                isAllElements={isAllElements}
                                setIsAllElements={setIsAllElements}
                            />
                        )}
                        {step === STEP.GROUP_STEP && (
                            <GroupForm
                                danger={danger}
                                setDanger={setDanger}
                                readMode={readMode}
                            />
                        )}
                        {step === STEP.MASTERY_STEP && (
                            <MasteryForm
                                danger={danger}
                                setDanger={setDanger}
                                readMode={readMode}
                            />
                        )}
                    </>
                )
            }
        </StepperDialog>
    )
}

DangerDialog.propTypes = {
    danger: PropTypes.instanceOf(DtoDangerTable),
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    dispatchStateAUD: PropTypes.func,
    readMode: PropTypes.bool,
}

export default DangerDialog
